import { useEffect, useRef, useState } from "react";
import { Camera, CheckCircle2, Loader2, ShieldCheck, X } from "lucide-react";
import Human from "@vladmandic/human";
import api from "../../api/axios";

const human = new Human({
  modelBasePath: "/models/",
  cacheSensitivity: 0,
  face: {
    enabled: true,
    // Selfie attendance always uses the front camera, so rotation detection only
    // adds latency without improving this straight-on capture flow.
    detector: { enabled: true, rotation: false, maxDetected: 1, minConfidence: 0.65 },
    mesh: { enabled: true },
    // Iris distance is optional for identity matching and can return incomplete
    // landmark arrays on some mobile/browser camera combinations.
    iris: { enabled: false },
    description: { enabled: true },
    antispoof: { enabled: true },
    liveness: { enabled: true },
    emotion: { enabled: false },
  },
  body: { enabled: false },
  hand: { enabled: false },
  object: { enabled: false },
  gesture: { enabled: false },
});

const capturePhoto = (video) => {
  const canvas = document.createElement("canvas");
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  canvas.getContext("2d").drawImage(video, 0, 0);
  return canvas.toDataURL("image/jpeg", 0.84);
};

export default function SelfieAttendanceModal({ action, registered, onRegistered, onVerified, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const captureRef = useRef(null);
  const autoCaptureTimerRef = useRef(null);
  const autoCaptureAttemptsRef = useRef(0);
  const modalActiveRef = useRef(false);
  const [stage, setStage] = useState("camera");
  const [message, setMessage] = useState("Position your face clearly and look directly at the camera.");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    modalActiveRef.current = true;
    const start = async () => {
      try {
        setMessage("Loading secure face recognition…");
        
        // Start camera and load ML model in parallel to eliminate startup lag
        const streamPromise = navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        });
        
        await human.load();
        const stream = await streamPromise;
        
        if (!active) return stream.getTracks().forEach((track) => track.stop());
        streamRef.current = stream;
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setMessage(registered ? "Blink once, then keep your face centered." : "Register your face in clear light.");
        if (registered) {
          // Start immediately once the camera delivers a stable frame
          autoCaptureTimerRef.current = window.setTimeout(() => captureRef.current?.(true), 100);
        }
      } catch (error) {
        setMessage(error.message || "Camera access is required for selfie attendance.");
        setStage("error");
      }
    };
    start();
    return () => {
      active = false;
      modalActiveRef.current = false;
      window.clearTimeout(autoCaptureTimerRef.current);
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, [registered]);

  const capture = async (automatic = false) => {
    if (!videoRef.current || busy) return;
    setBusy(true);
    setMessage("Checking face and liveness…");
    try {
      const result = await human.detect(videoRef.current);
      if (result.face.length !== 1) throw new Error("Keep exactly one face visible in the camera.");
      const face = result.face[0];
      if (!face.embedding?.length) throw new Error("Face details were unclear. Move closer and try again.");
      if ((face.faceScore || face.boxScore || 0) < 0.65) throw new Error("Face is not clear enough. Use better lighting.");
      if (face.real !== undefined && face.real < 0.55) throw new Error("Live selfie check failed. Do not use a photo or screen.");
      if (face.live !== undefined && face.live < 0.5) throw new Error("Please blink and move your head slightly, then retry.");

      const photoData = capturePhoto(videoRef.current);
      const descriptor = Array.from(face.embedding);
      if (!registered) {
        await api.post("/api/employee/attendance/face/register", { descriptor, photoData });
        onRegistered?.();
        setStage("success");
        setMessage("Face registered securely in Firebase. You can now use selfie attendance.");
        return;
      }

      const { data } = await api.post("/api/employee/attendance/face/verify", {
        descriptor, photoData, action,
      });
      streamRef.current?.getTracks().forEach((track) => track.stop());
      onVerified?.(data.selfieToken, photoData);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Selfie verification failed.";
      // If auto-detect got an early/unclear camera frame, keep scanning rather
      // than making the employee press a button. Server verification failures
      // still require a deliberate retry so they remain visible to the user.
      if (automatic && !error.response && modalActiveRef.current) {
        if (autoCaptureAttemptsRef.current < 10) {
          autoCaptureAttemptsRef.current += 1;
          setStage("camera");
          setMessage("Looking for a clear live face…");
          autoCaptureTimerRef.current = window.setTimeout(() => captureRef.current?.(true), 100);
        } else {
          setStage("error");
          setMessage("Auto-detect couldn't clearly see your face. Please capture manually.");
        }
      } else {
        setStage("error");
        setMessage(errorMessage);
      }
    } finally {
      setBusy(false);
    }
  };
  captureRef.current = capture;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-100 p-6">
          <div>
            <span className="text-xs font-extrabold uppercase tracking-widest text-violet-600">Firebase selfie attendance</span>
            <h2 className="mt-1 text-2xl font-black text-slate-900">{registered ? "Verify your face" : "Register your face"}</h2>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl bg-slate-100 p-2 text-slate-600"><X size={20} /></button>
        </div>
        <div className="p-6">
          <div className="relative aspect-square max-h-[390px] overflow-hidden rounded-3xl bg-slate-950">
            <video ref={videoRef} muted playsInline className="h-full w-full scale-x-[-1] object-cover" />
            <div className="pointer-events-none absolute inset-[12%] rounded-[45%] border-4 border-white/80 shadow-[0_0_0_999px_rgba(0,0,0,.2)]" />
            {stage === "success" && <div className="absolute inset-0 flex items-center justify-center bg-emerald-600/85"><CheckCircle2 size={70} className="text-white" /></div>}
          </div>
          <div className={`mt-4 flex gap-3 rounded-2xl p-4 text-sm font-semibold ${stage === "error" ? "bg-red-50 text-red-700" : "bg-violet-50 text-violet-800"}`}>
            <ShieldCheck className="shrink-0" size={20} /><span>{message}</span>
          </div>
          <p className="mt-3 text-xs text-slate-500">Reference face and attendance selfies are retained in Firebase Storage only. They are not saved in the attendance database.</p>
          <div className="mt-5 flex justify-end gap-3">
            <button type="button" onClick={onClose} className="rounded-xl px-5 py-3 font-bold text-slate-600">Cancel</button>
            {stage !== "success" && <button type="button" disabled={busy} onClick={capture} className="flex items-center gap-2 rounded-xl bg-violet-600 px-5 py-3 font-bold text-white disabled:opacity-60">
              {busy ? <Loader2 size={18} className="animate-spin" /> : <Camera size={18} />}{registered ? "Capture & Verify now" : "Register face"}
            </button>}
          </div>
        </div>
      </div>
    </div>
  );
}
